// @djsem/ui — API-result → UI-state mapping + a framework-free async resource state machine
// (fix report Phase 4 / CLAUDE_04). This is the single place where every DJSEMApiResult is
// turned into one of the canonical prompt-89 UI states, so every screen renders consistently.

import type { DJSEMApiResult } from "./apiClient";

/** The canonical prompt-89 UI states every screen must be able to render. */
export type DJSEMUiState =
  | "loading"
  | "ready"
  | "empty"
  | "error"
  | "offline"
  | "permission_denied"
  | "plan_locked"
  | "rights_blocked"
  | "job_failed";

/** A resolved UI state plus its data (when ready/empty) or a human-readable message (otherwise). */
export interface UiStateView<T> {
  state: DJSEMUiState;
  data?: T;
  message?: string;
}

/**
 * Map a settled API result to a UI state. `isEmpty` lets a screen distinguish ready-but-empty
 * (render EmptyState) from ready-with-data. Error codes from the client/worker route to the
 * matching canonical state.
 * @param result - The settled {@link DJSEMApiResult} to map.
 * @param isEmpty - Optional predicate deciding whether ok data should map to `"empty"` instead of `"ready"`.
 * @returns The corresponding {@link UiStateView}.
 */
export function mapResultToUiState<T>(
  result: DJSEMApiResult<T>,
  isEmpty: (data: T) => boolean = () => false,
): UiStateView<T> {
  if (result.ok) {
    return isEmpty(result.data) ? { state: "empty", data: result.data } : { state: "ready", data: result.data };
  }
  const code = result.error.code;
  const msg = result.error.message;
  switch (code) {
    case "network_error": return { state: "offline", message: msg };
    case "unauthenticated": return { state: "permission_denied", message: "Please sign in." };
    case "forbidden": return { state: "permission_denied", message: msg };
    case "plan_locked": return { state: "plan_locked", message: msg };
    case "rights_blocked": return { state: "rights_blocked", message: msg };
    case "job_failed": return { state: "job_failed", message: msg };
    default: return { state: "error", message: msg };
  }
}

// ---- Async resource state machine (framework-free; hooks wrap this) ----

/** The state machine behind every async resource load: current UI view + whether it's in flight. */
export interface AsyncResource<T> {
  view: UiStateView<T>;
  loading: boolean;
}

/** @returns A fresh {@link AsyncResource} in the `"loading"` state. */
export function loadingResource<T>(): AsyncResource<T> {
  return { view: { state: "loading" }, loading: true };
}

/**
 * Run a fetcher to completion and produce the resulting {@link AsyncResource}.
 * @param fetcher - Called to load the resource; must resolve to a {@link DJSEMApiResult}.
 * @param isEmpty - Optional predicate distinguishing ready-but-empty data from ready-with-data.
 * @returns The settled {@link AsyncResource} (never in the `"loading"` state).
 */
export async function loadResource<T>(
  fetcher: () => Promise<DJSEMApiResult<T>>,
  isEmpty?: (data: T) => boolean,
): Promise<AsyncResource<T>> {
  const result = await fetcher();
  return { view: mapResultToUiState(result, isEmpty), loading: false };
}

/**
 * Derive shell badge counts from real data, degrading gracefully to 0 on any non-ready state
 * (so an offline shell shows 0 pending rather than crashing).
 * @param res - The list resource (e.g. notifications/tasks) to read a count from.
 * @returns The item count when ready/empty, otherwise 0.
 */
export function badgeCount<T>(res: AsyncResource<{ items: T[] }>): number {
  return res.view.state === "ready" || res.view.state === "empty" ? (res.view.data?.items.length ?? 0) : 0;
}
